// PRD.305 — Laboratory Workflow & Patient Labs: pure domain logic.
//
// Everything here is dependency-free (no workspace-package imports) so it runs
// under the node:test harness and is shared verbatim by the Laboratory module,
// Patient Workspace Labs, Consultation Labs and the Files lock.
//
// Three ideas carry the whole model:
//
//   1. STATUS LIVES ON THE TEST. A lab order groups tests that progress
//      independently, so the order has no status column at all — its display
//      status is derived here (`aggregateLabOrderStatus`) and can never drift.
//
//   2. PUBLICATION LIVES ON THE RESULT, not on the uploaded file. MVP results
//      are file uploads, but structured Forms are a planned future source; a
//      Forms-backed result publishes through the identical guards with zero
//      artifacts attached.
//
//   3. PUBLISHED IS IMMUTABLE. Corrections append a new result that supersedes
//      the old one. Nothing published is ever edited or deleted.
//
// The repository has no state-machine framework and none is introduced here.
// The convention followed is the one used by delivery-order-logic.ts and
// patient-packages-logic.ts: pure `canX(...) => { ok, reason }` predicates that
// a service layer consults before issuing a direct UPDATE.

// ─── Per-test status (PRD §5.1) ───

export type LabTestStatus =
  | 'ordered'
  | 'collected'
  | 'in_progress'
  | 'result_ready'
  | 'published'
  | 'cancelled';

/** Canonical workflow order. `cancelled` is terminal and off-ladder. */
export const LAB_TEST_STATUSES: readonly LabTestStatus[] = [
  'ordered',
  'collected',
  'in_progress',
  'result_ready',
  'published',
  'cancelled',
] as const;

export const LAB_TEST_STATUS_LABELS: Record<LabTestStatus, string> = {
  ordered: 'Ordered',
  collected: 'Collected',
  in_progress: 'In Progress',
  result_ready: 'Result Ready',
  published: 'Published',
  cancelled: 'Cancelled',
};

/**
 * Position on the processing ladder. `cancelled` is deliberately absent — it is
 * not "further along", it is out of the flow, and every aggregation below
 * excludes it before ranking.
 */
const LAB_TEST_STATUS_RANK: Record<Exclude<LabTestStatus, 'cancelled'>, number> = {
  ordered: 0,
  collected: 1,
  in_progress: 2,
  result_ready: 3,
  published: 4,
};

/** Rank of a non-cancelled status (0..4). */
export function labTestStatusRank(status: LabTestStatus): number {
  if (status === 'cancelled') return -1;
  return LAB_TEST_STATUS_RANK[status];
}

/**
 * Coerce any stored/untrusted value to a valid status. Unknown, null and
 * legacy values read as `ordered` — the safest interpretation, since it is the
 * least-advanced state and therefore never claims work that has not happened.
 */
export function normalizeLabTestStatus(value: unknown): LabTestStatus {
  return typeof value === 'string' && (LAB_TEST_STATUSES as readonly string[]).includes(value)
    ? (value as LabTestStatus)
    : 'ordered';
}

/** The minimal shape aggregation needs. Real rows satisfy it structurally. */
export interface LabTestStatusSource {
  status?: string | null;
}

// ─── Aggregate order status (PRD §5.2) ───

/**
 * Derive an order's display status from its tests. DERIVED, never stored:
 * PRD §5.2 forbids a second independently editable aggregate that could drift.
 *
 * One rule does all of it:
 *
 *   no tests                 → 'ordered'
 *   every test cancelled     → 'cancelled'
 *   otherwise                → the LEAST-ADVANCED non-cancelled test's status
 *
 * That single rule reproduces each of the PRD's four requirements:
 *
 *   - all cancelled → Cancelled                       (explicit branch)
 *   - all published/cancelled, >=1 published →
 *     Published                                       (min over non-cancelled = published)
 *   - any result ready while none remain earlier in
 *     processing → Result Ready                       (min = result_ready)
 *   - otherwise the furthest meaningful incomplete
 *     state WITHOUT hiding tests still behind         (min = the laggard)
 *
 * Total, deterministic and order-independent: shuffling the input cannot change
 * the answer, because `min` is commutative.
 */
export function aggregateLabOrderStatus(tests: readonly LabTestStatusSource[]): LabTestStatus {
  if (tests.length === 0) return 'ordered';

  const active = tests
    .map((t) => normalizeLabTestStatus(t.status))
    .filter((s) => s !== 'cancelled') as Exclude<LabTestStatus, 'cancelled'>[];

  if (active.length === 0) return 'cancelled';

  return active.reduce((lowest, s) =>
    labTestStatusRank(s) < labTestStatusRank(lowest) ? s : lowest,
  );
}

/**
 * Whether an order still needs laboratory attention — i.e. its aggregate is
 * neither fully published nor fully cancelled. Drives the Laboratory queue's
 * default "open work" filter.
 */
export function isLabOrderOpen(tests: readonly LabTestStatusSource[]): boolean {
  const aggregate = aggregateLabOrderStatus(tests);
  return aggregate !== 'published' && aggregate !== 'cancelled';
}

// ─── Guards ───

/** Guard result, matching delivery-order-logic.ts / patient-packages-logic.ts. */
export interface LabGuardResult {
  ok: boolean;
  reason?: string;
}

const OK: LabGuardResult = { ok: true };
const deny = (reason: string): LabGuardResult => ({ ok: false, reason });

/**
 * Advance a test one or more steps along the ladder.
 *
 * Forward-only: a test never moves backwards, out of `cancelled`, or out of
 * `published`. `published` is reached only through `publishLabResult`, never by
 * a plain status advance — publication is an explicit act on a result, and
 * routing it through here would let a test claim published state with no
 * result behind it.
 */
export function canAdvanceLabTest(test: LabTestStatusSource, to: LabTestStatus): LabGuardResult {
  const from = normalizeLabTestStatus(test.status);
  if (from === 'cancelled') return deny('This test was cancelled.');
  if (from === 'published') return deny('This test is published and can no longer change.');
  if (to === 'cancelled') return deny('Use cancelLabTest to cancel a test.');
  if (to === 'published') return deny('Publish a result to move a test to Published.');
  if (labTestStatusRank(to) <= labTestStatusRank(from)) {
    return deny(`A test cannot move back from ${LAB_TEST_STATUS_LABELS[from]}.`);
  }
  return OK;
}

/** Cancel an ordered test. Published work is a clinical record and stays. */
export function canCancelLabTest(test: LabTestStatusSource): LabGuardResult {
  const status = normalizeLabTestStatus(test.status);
  if (status === 'published') return deny('A published test cannot be cancelled.');
  if (status === 'cancelled') return deny('This test is already cancelled.');
  return OK;
}

/** Attach a result artifact. Allowed at any live stage; blocked once terminal. */
export function canUploadLabResult(test: LabTestStatusSource): LabGuardResult {
  const status = normalizeLabTestStatus(test.status);
  if (status === 'cancelled') return deny('This test was cancelled.');
  if (status === 'published') return deny('This test is published — upload a correction instead.');
  return OK;
}

/** The minimal `lab_results` shape the guards need. */
export interface LabResultSource {
  published_at?: string | null;
  superseded_at?: string | null;
  result_ready_at?: string | null;
}

export function isLabResultPublished(result: LabResultSource): boolean {
  return result.published_at != null;
}

export function isLabResultSuperseded(result: LabResultSource): boolean {
  return result.superseded_at != null;
}

/**
 * Publish a result (PRD §6.2). Explicit, separate from upload, and requires:
 *   - a live test,
 *   - the test marked Result Ready (upload alone is not readiness),
 *   - a result that is not already published,
 *   - something to publish: at least one artifact, OR a non-file result source
 *     (`form` / `manual`), so the future Forms path needs no new guard.
 */
export function canPublishLabResult(input: {
  test: LabTestStatusSource;
  result: LabResultSource & { source?: string | null };
  attachedFileCount: number;
}): LabGuardResult {
  const status = normalizeLabTestStatus(input.test.status);
  if (status === 'cancelled') return deny('This test was cancelled.');
  if (isLabResultPublished(input.result)) return deny('This result is already published.');
  if (status !== 'result_ready') {
    return deny('Mark the test Result Ready before publishing.');
  }
  const source = input.result.source ?? 'file_upload';
  if (source === 'file_upload' && input.attachedFileCount < 1) {
    return deny('Attach at least one result file before publishing.');
  }
  return OK;
}

/**
 * PRD §6.3 — a published result is never replaced or edited in place. Always
 * denies; exists so callers express the rule by name rather than by comment.
 */
export function canReplacePublishedResult(): LabGuardResult {
  return deny('Published results are immutable. Publish a correction instead.');
}

/**
 * Issue a correction. Only a published, not-yet-superseded result can be
 * corrected — correcting a draft means editing it, and correcting an already
 * superseded result would fork the chain.
 */
export function canCorrectLabResult(result: LabResultSource): LabGuardResult {
  if (!isLabResultPublished(result)) return deny('Only a published result can be corrected.');
  if (isLabResultSuperseded(result)) {
    return deny('This result has already been corrected by a newer result.');
  }
  return OK;
}

/** Remove an artifact. Permitted only while its result is still a draft. */
export function canDeleteLabResultFile(result: LabResultSource): LabGuardResult {
  if (isLabResultPublished(result)) {
    return deny('This file belongs to a published result and cannot be removed.');
  }
  return OK;
}

// ─── Patient Workspace access modes (PRD §8.1, §12) ───

export type LaboratoryPatientWorkspaceAccess =
  | 'full_management'
  | 'monitor_results'
  | 'published_only';

export const LABORATORY_ACCESS_MODES: readonly LaboratoryPatientWorkspaceAccess[] = [
  'full_management',
  'monitor_results',
  'published_only',
] as const;

/** Exact PRD labels. */
export const LABORATORY_ACCESS_MODE_LABELS: Record<LaboratoryPatientWorkspaceAccess, string> = {
  full_management: 'Full Management',
  monitor_results: 'Monitor + Results',
  published_only: 'Published Results Only',
};

export const LABORATORY_ACCESS_MODE_DESCRIPTIONS: Record<LaboratoryPatientWorkspaceAccess, string> =
  {
    full_management:
      'Small clinics with no dedicated laboratory station. Patient Workspace can create orders, advance status, upload and publish results — always subject to permissions.',
    monitor_results:
      'Recommended for hybrid clinics. Patient Workspace monitors status and reads results; operational work stays in the Laboratory module.',
    published_only:
      'Patient Workspace shows published results only, with no in-progress laboratory workflow.',
  };

/** PRD §12 / §20 — the backward-compatible default for every existing clinic. */
export const DEFAULT_LABORATORY_ACCESS: LaboratoryPatientWorkspaceAccess = 'monitor_results';

/** RBAC inputs, already resolved by the caller from the permission set. */
export interface LaboratoryPermissions {
  /** `Laboratory | Lab Orders` >= view */
  viewOrders: boolean;
  /** `Laboratory | Lab Orders` >= edit */
  createOrders: boolean;
  /** `Laboratory | Lab Orders` = full */
  manageOrders: boolean;
  /** `Laboratory | Lab Results` >= view */
  viewResults: boolean;
  /** `Laboratory | Lab Results` >= edit */
  manageWorkflow: boolean;
  /** `Laboratory | Lab Results` = full */
  publishResults: boolean;
}

/** Every capability a laboratory surface can offer. */
export interface LaboratoryCapabilities {
  /** See published results and open their files. */
  viewPublished: boolean;
  /** See in-progress orders/tests and their workflow state. */
  viewOperational: boolean;
  createOrder: boolean;
  cancelOrder: boolean;
  advanceStatus: boolean;
  uploadResult: boolean;
  publishResult: boolean;
}

const NO_CAPABILITIES: LaboratoryCapabilities = {
  viewPublished: false,
  viewOperational: false,
  createOrder: false,
  cancelOrder: false,
  advanceStatus: false,
  uploadResult: false,
  publishResult: false,
};

export type LaboratorySurface = 'laboratory' | 'patient_workspace' | 'consultation';

/**
 * The single resolver for "what may this user do with laboratory work HERE?".
 *
 * PRD §8.2 — configuration is not authorization. Effective access is the
 * INTERSECTION of module availability, the Laboratory Settings mode, and RBAC.
 * Selecting Full Management can only ever remove restrictions the mode itself
 * imposed; it can never hand a user a permission they do not hold, which is why
 * every operational capability below is ANDed with its permission flag.
 *
 * PRD.305 D5 — when the clinic disables the Laboratory main module, only
 * OPERATIONAL surfaces go away. Clinical history does not: the clinical
 * surfaces fall back to `published_only` rather than to nothing, so previously
 * published results stay readable in Patient Workspace, Consultation and Files.
 */
export function resolveLaboratoryCapabilities(input: {
  surface: LaboratorySurface;
  moduleEnabled: boolean;
  mode: LaboratoryPatientWorkspaceAccess;
  permissions: LaboratoryPermissions;
}): LaboratoryCapabilities {
  const { surface, moduleEnabled, permissions: p } = input;

  // The dedicated module IS the operational surface — it disappears entirely
  // with the module and is not shaped by the Patient Workspace access mode.
  if (surface === 'laboratory') {
    if (!moduleEnabled) return NO_CAPABILITIES;
    return {
      viewPublished: p.viewResults,
      viewOperational: p.viewOrders,
      createOrder: p.createOrders,
      cancelOrder: p.manageOrders,
      advanceStatus: p.manageWorkflow,
      uploadResult: p.manageWorkflow,
      publishResult: p.publishResults,
    };
  }

  // Clinical surfaces: a disabled module degrades to published-history-only.
  const mode: LaboratoryPatientWorkspaceAccess = moduleEnabled ? input.mode : 'published_only';

  if (mode === 'published_only') {
    return { ...NO_CAPABILITIES, viewPublished: p.viewResults };
  }

  if (mode === 'monitor_results') {
    return {
      ...NO_CAPABILITIES,
      viewPublished: p.viewResults,
      viewOperational: p.viewOrders,
    };
  }

  // full_management — every operation still gated on its own permission.
  return {
    viewPublished: p.viewResults,
    viewOperational: p.viewOrders,
    createOrder: p.createOrders,
    cancelOrder: p.manageOrders,
    advanceStatus: p.manageWorkflow,
    uploadResult: p.manageWorkflow,
    publishResult: p.publishResults,
  };
}

/** True if a surface has nothing at all to show and should be hidden. */
export function isLaboratorySurfaceEmpty(caps: LaboratoryCapabilities): boolean {
  return !caps.viewPublished && !caps.viewOperational;
}

// ─── Catalog classification (PRD §11) ───

/**
 * Category names that HINT a service is a laboratory test.
 *
 * Read this carefully, because the distinction is the whole point:
 *
 *   `appointment_services.is_lab_test` is the CANONICAL classification.
 *   Nothing in the app resolves the laboratory catalog by category —
 *   `qLabTestCatalog` filters on the flag alone.
 *
 * This list exists ONLY to pre-fill the toggle in the Manage Services drawer
 * when someone picks the seeded "Lab Work" category, so the common case needs
 * one click instead of two. A clinic may rename or delete that category at any
 * time; if it does, this stops offering a suggestion and NOTHING about already
 * saved services changes.
 */
const LAB_TEST_CATEGORY_HINTS: readonly string[] = ['lab work', 'laboratory', 'labs', 'lab'];

/** Whether a category name hints at laboratory work. Convenience only. */
export function isLabTestCategoryName(name: string | null | undefined): boolean {
  if (!name) return false;
  return LAB_TEST_CATEGORY_HINTS.includes(name.trim().toLowerCase());
}

/**
 * Resolve what the "Laboratory Test" toggle should show as the user edits a
 * service. Pure, so the rule that protects saved data is unit-tested rather
 * than trusted to an effect in a drawer.
 *
 * Two invariants matter more than the convenience:
 *
 *   1. EDITING NEVER AUTO-CHANGES A SAVED VALUE. Re-categorising, renaming or
 *      re-pricing an existing service leaves `is_lab_test` exactly as stored.
 *      A service explicitly marked as a lab test does not silently stop being
 *      one because someone moved it to a different category.
 *
 *   2. AN EXPLICIT CHOICE WINS. Once the user has touched the toggle on a new
 *      service, category changes stop overriding them.
 *
 * Only an untouched toggle on a NEW service follows the category.
 */
export function resolveLabTestSuggestion(input: {
  /** 'edit' means an existing, already-persisted service. */
  mode: 'create' | 'edit';
  /** Name of the currently selected category, if any. */
  categoryName: string | null | undefined;
  /** Whether the user has explicitly set the toggle in this session. */
  userTouched: boolean;
  /** The toggle's current value. */
  current: boolean;
}): boolean {
  if (input.mode === 'edit') return input.current; // invariant 1
  if (input.userTouched) return input.current; // invariant 2
  return isLabTestCategoryName(input.categoryName);
}

// ─── Billing (PRD §11) ───

/**
 * `invoice_items.source_type` for a laboratory test. Laboratory bills through
 * the EXISTING invoice path — there is no laboratory invoice or payment table.
 */
export const LAB_TEST_INVOICE_SOURCE_TYPE = 'lab_test';

/**
 * Deterministic `invoice_items.id` for a billed test, mirroring PRD.270's
 * walk-in import: two clients billing the same test (or an upload retry)
 * produce the SAME id and converge through `ON CONFLICT (id) DO UPDATE`, so a
 * duplicate line is impossible without a partial unique index.
 */
export function labInvoiceItemId(labOrderTestId: string): string {
  return `lab-${labOrderTestId}`;
}

/** The ordered-test fields billing needs. */
export interface LabTestBillingSource {
  id: string;
  service_id?: string | null;
  test_name?: string | null;
  price_cents_snapshot?: number | null;
  status?: string | null;
}

/** An invoice line ready to insert through the existing billing path. */
export interface LabInvoiceLineDraft {
  id: string;
  service_id: string | null;
  service_name: string;
  qty: number;
  unit_price: number;
  source_type: string;
  source_id: string;
}

/**
 * Map an ordered test onto an invoice line. Price comes from the snapshot taken
 * at ordering time (integer cents → major units), so re-pricing the catalog
 * later never rewrites an existing order's charge.
 */
export function toLabInvoiceLine(test: LabTestBillingSource): LabInvoiceLineDraft {
  const cents = Number(test.price_cents_snapshot ?? 0);
  return {
    id: labInvoiceItemId(test.id),
    service_id: test.service_id ?? null,
    service_name: test.test_name ?? 'Laboratory test',
    qty: 1,
    unit_price: Number.isFinite(cents) ? cents / 100 : 0,
    source_type: LAB_TEST_INVOICE_SOURCE_TYPE,
    source_id: test.id,
  };
}

/** A cancelled test is not billable; everything else is. */
export function isLabTestBillable(test: LabTestBillingSource): boolean {
  return normalizeLabTestStatus(test.status) !== 'cancelled';
}

// ─── Result artifacts (PRD §10) ───

/** Joined `lab_result_files` + `patient_files` row. */
export interface LabResultArtifactSource {
  id: string;
  lab_result_id?: string | null;
  patient_file_id?: string | null;
  filename?: string | null;
  url?: string | null;
  mime_type?: string | null;
  sort_order?: number | null;
  published_at?: string | null;
}

export interface LabResultArtifact {
  /** `lab_result_files.id` — the linkage row. */
  linkId: string;
  /** `patient_files.id` — THE artifact. Identical in Labs and in Files. */
  fileId: string;
  resultId: string | null;
  filename: string;
  url: string | null;
  mimeType: string | null;
  sortOrder: number;
  published: boolean;
}

/**
 * Normalize a joined row into the shape both Labs and Files render.
 *
 * `fileId` is deliberately `patient_files.id`, not the link id: opening a
 * document from Labs and from Files must resolve to the same stored object, so
 * there is exactly one physical upload no matter how many surfaces show it.
 * Rows with no linked file are dropped — a dangling link has nothing to open.
 */
export function toLabResultArtifact(row: LabResultArtifactSource): LabResultArtifact | null {
  if (!row.patient_file_id) return null;
  return {
    linkId: row.id,
    fileId: row.patient_file_id,
    resultId: row.lab_result_id ?? null,
    filename: row.filename ?? 'Result file',
    url: row.url ?? null,
    mimeType: row.mime_type ?? null,
    sortOrder: Number(row.sort_order ?? 0),
    published: row.published_at != null,
  };
}

/**
 * Split artifacts by publication so a surface can render "available to
 * clinicians" separately from "uploaded, awaiting publication" — the visual
 * distinction PRD §17 requires.
 */
export function partitionLabArtifacts(rows: readonly LabResultArtifactSource[]): {
  published: LabResultArtifact[];
  unpublished: LabResultArtifact[];
} {
  const published: LabResultArtifact[] = [];
  const unpublished: LabResultArtifact[] = [];
  for (const row of rows) {
    const artifact = toLabResultArtifact(row);
    if (!artifact) continue;
    (artifact.published ? published : unpublished).push(artifact);
  }
  const bySort = (a: LabResultArtifact, b: LabResultArtifact) => a.sortOrder - b.sortOrder;
  return { published: published.sort(bySort), unpublished: unpublished.sort(bySort) };
}

// ─── Visit / encounter scoping (PRD §9) ───

/** The order fields scoping needs. */
export interface LabOrderScopeSource {
  id: string;
  encounter_id?: string | null;
  visit_id?: string | null;
}

/**
 * Orders belonging to one consultation. Consultation Labs is visit-centric, so
 * an order raised from the Patient Workspace with no encounter (a very common
 * case) is correctly absent here while still appearing in Patient Labs.
 */
export function filterOrdersForEncounter<T extends LabOrderScopeSource>(
  orders: readonly T[],
  encounterId: string | null,
): T[] {
  if (!encounterId) return [];
  return orders.filter((o) => o.encounter_id === encounterId);
}

/** Orders belonging to a visit — broader than an encounter, used by summaries. */
export function filterOrdersForVisit<T extends LabOrderScopeSource>(
  orders: readonly T[],
  visitId: string | null,
): T[] {
  if (!visitId) return [];
  return orders.filter((o) => o.visit_id === visitId);
}
